import { useResizeDetector } from 'react-resize-detector';
import { VariableSizeList } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import { CSSProperties, useCallback, useEffect, useRef } from 'react';

import { BottomButton } from '@@/LogViewer/LogList/BottomButton';
import { LogInterface, ProcessedLogsInterface } from '@@/LogViewer/types';
import { LogRow } from '@@/LogViewer/LogList/LogRow/LogRow';
import { useFocusKeyword } from '@@/LogViewer/hooks/useFocusKeyword';
import { useKeepAtBottom } from '@@/LogViewer/hooks/useKeepAtBottom';

import { useLogViewerContext } from '../context';
import { DEFAULT_ROW_SIZE, useSizeCache } from '../hooks/useSizeCache';

interface Props {
  logs: ProcessedLogsInterface;
}

// Appended lines leave every existing index pointing at the same content, so the
// virtualiser's measured offsets stay valid. Only a shifted window invalidates them:
// the tail limit dropping old lines, a filter change, or a different resource.
export function isAppendOnly(prev: LogInterface[], next: LogInterface[]) {
  if (next.length < prev.length) {
    return false;
  }
  if (prev.length === 0) {
    return true;
  }

  // Comparing all of them every poll would defeat the point, so sample the ends and the
  // middle: enough to catch a window that slid or a wholesale replacement. A false
  // positive needs all three samples to match while other lines moved, and identical
  // sampled lines imply identical measured heights anyway.
  const middle = Math.floor((prev.length - 1) / 2);
  return (
    prev[0].line === next[0].line &&
    prev[middle].line === next[middle]?.line &&
    prev[prev.length - 1].line === next[prev.length - 1]?.line
  );
}

export function LogList({ logs }: Props) {
  const { controllerState } = useLogViewerContext();
  const listRef = useRef<VariableSizeList>(null);
  const outerRef = useRef<HTMLDivElement>(null);
  const { getSize, resetCache } = useSizeCache();

  // Drops the virtualiser's measured row offsets. Every unmeasured row then falls back
  // to estimatedItemSize, so the container's reported height changes unless the estimate
  // is accurate - see the estimatedItemSize prop below.
  const resetOffsets = useCallback(() => {
    listRef.current?.resetAfterIndex(0);
  }, []);

  const listReset = useCallback(() => {
    // reset cache clears the cache list of log-row heights
    resetCache();
    resetOffsets();
  }, [resetCache, resetOffsets]);

  useFocusKeyword(listRef);

  const { isScrollAtBottom, onItemsRendered, onScroll, scrollToBottom } =
    useKeepAtBottom(outerRef, listRef);

  useResizeDetector({
    targetRef: outerRef,
    onResize: listReset,
    skipOnMount: true,
    refreshMode: 'debounce',
    refreshRate: 100,
  });

  // Wrap mode and the line-number gutter are the only controller options that change how
  // tall a given line renders, so they are the only ones that invalidate measurements.
  useEffect(() => {
    listReset();
  }, [controllerState.wrapLine, controllerState.showLineNumbers, listReset]);

  // Auto-refresh polls every 5s and used to reset on every tick. Because offsets are
  // cumulative, a reset forces a synchronous re-measure of every row up to the viewport -
  // hundreds of forced reflows per tick while the user is trying to read. Skip it when
  // indices did not actually move.
  const prevLogsRef = useRef<LogInterface[]>([]);
  useEffect(() => {
    const prevLogs = prevLogsRef.current;
    prevLogsRef.current = logs.logs;

    if (!isAppendOnly(prevLogs, logs.logs)) {
      resetOffsets();
    }
  }, [logs.logs, resetOffsets]);

  return (
    <>
      <AutoSizer defaultHeight={750} disableWidth className="h-full">
        {({ height }) => (
          <VariableSizeList
            height={height}
            itemCount={logs.logs.length}
            itemSize={(index: number) =>
              getSize(
                index,
                logs,
                outerRef,
                controllerState.wrapLine,
                controllerState.showLineNumbers
              )
            }
            // react-window's built-in default is 50px, but a row is 25px per visual line,
            // so a single-line log reports roughly twice its real height and then shrinks
            // as rows get measured. The browser resolves a shrinking scrollHeight by
            // clamping scrollTop, which lands the reader at the bottom.
            //
            // DEFAULT_ROW_SIZE is the *minimum* a row can be, not the average - multi-line
            // rows are taller. That is the point: underestimating means the reported height
            // only ever grows as rows get measured, so scrollTop stays valid and is never
            // clamped. Anything higher risks overestimating and brings the jump back.
            estimatedItemSize={DEFAULT_ROW_SIZE}
            width="100%"
            onScroll={onScroll}
            onItemsRendered={onItemsRendered}
            ref={listRef}
            outerRef={outerRef}
            className="log-list"
            overscanCount={10}
          >
            {renderItem}
          </VariableSizeList>
        )}
      </AutoSizer>
      <BottomButton visible={!isScrollAtBottom} onClick={scrollToBottom} />
    </>
  );
}

function renderItem({ index, style }: { index: number; style: CSSProperties }) {
  return (
    <div style={{ ...style, width: 'unset' }}>
      <LogRow lineIndex={index} />
    </div>
  );
}
